import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { TOOLS, CATEGORIES } from "../data/tools";
import { ToolCard } from "../components/ToolCard";
import { Seo } from "../components/Seo";

export default function CatalogPage() {
    const [params, setParams] = useSearchParams();
    const [query, setQuery] = useState(params.get("q") || "");
    const [category, setCategory] = useState("todas");

    useEffect(() => {
        setQuery(params.get("q") || "");
    }, [params]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return TOOLS.filter((t) => {
            const inCategory = category === "todas" || t.category === category;
            if (!inCategory) return false;
            if (!q) return true;
            return (
                t.name.toLowerCase().includes(q) ||
                t.desc.toLowerCase().includes(q) ||
                t.keywords.some((k) => k.includes(q) || q.includes(k))
            );
        });
    }, [query, category]);

    const onQuery = (v) => {
        setQuery(v);
        setParams(v ? { q: v } : {}, { replace: true });
    };

    return (
        <main className="py-14 px-6 max-w-6xl mx-auto min-h-[60vh]">
            <Seo
                title="Todas as ferramentas de PDF — ScorpionPDF"
                description="Catálogo completo: juntar, dividir, comprimir, girar, reordenar e remover páginas de PDF, e converter entre PDF e imagens. Grátis e sem cadastro."
            />
            <span className="font-mono-tag text-xs font-bold text-[#0284C7] uppercase">Catálogo</span>
            <h1 className="font-poster text-5xl md:text-6xl text-black mt-1">Todas as ferramentas</h1>
            <p className="text-gray-700 mt-3 max-w-2xl">
                Quinze ferramentas de PDF, gratuitas e sem cadastro — incluindo IA para resumir e conversar com
                documentos. Arquivos excluídos automaticamente em até 60 minutos.
            </p>

            <div className="mt-8 flex flex-col md:flex-row gap-4 md:items-center">
                <div className="flex items-center border-2 border-black bg-white flex-1 max-w-md" role="search">
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => onQuery(e.target.value)}
                        placeholder="Buscar: juntar, comprimir, converter…"
                        aria-label="Buscar ferramenta no catálogo"
                        data-testid="catalog-search-input"
                        className="flex-1 px-4 py-3 text-sm outline-none"
                    />
                    <span className="px-4 py-3 bg-black text-[#22D3EE]">
                        <Search className="w-4 h-4" aria-hidden="true" />
                    </span>
                </div>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar por categoria">
                    {[{ id: "todas", name: "Todas" }, ...CATEGORIES].map((c) => (
                        <button
                            key={c.id}
                            onClick={() => setCategory(c.id)}
                            data-testid={`category-${c.id}`}
                            aria-pressed={category === c.id}
                            className={`px-4 py-2 border-2 border-black font-mono-tag text-xs font-bold uppercase transition-colors ${
                                category === c.id ? "bg-black text-[#22D3EE]" : "bg-white text-black hover:bg-[#F0F9FF]"
                            }`}
                        >
                            {c.name}
                        </button>
                    ))}
                </div>
            </div>

            {filtered.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="catalog-grid">
                    {filtered.map((tool) => (
                        <ToolCard key={tool.slug} tool={tool} />
                    ))}
                </div>
            ) : (
                <div className="mt-10 border-2 border-black p-10 bg-white text-center" data-testid="catalog-empty">
                    <p className="font-poster text-2xl text-black">NENHUMA FERRAMENTA ENCONTRADA</p>
                    <p className="text-sm text-gray-600 mt-2">
                        Tente outro termo, como “juntar”, “comprimir” ou “imagem”.
                    </p>
                </div>
            )}
        </main>
    );
}
